import { useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import cn from 'classnames';
import { toast } from 'sonner';

import { useSelector } from 'react-redux';
import { useGetCountriesQuery } from '../../../../../redux/services/data/countriesApi';
import { useGetResidencesForCityQuery } from '../../../../../redux/services/data/residencesApi';
import { selectUserToken } from '../../../../../redux/features/user/userSelectors';
import { useGetCitiesForCountryQuery } from '../../../../../redux/services/data/citiesApi';
import { useGetDistrictsForCityQuery } from '../../../../../redux/services/data/districtsApi';
import { useGetStreetsForCityQuery } from '../../../../../redux/services/data/streetsApi';
import { useGetStreetsNumberForStreetQuery } from '../../../../../redux/services/data/streetsNumberApi';

import { Trash } from 'lucide-react';
import { AccordionInputs } from '../../../AccordionInputs';
import { toContactLinks } from '../ContactsBlockForm';
import { RealtyMapPinInfo } from '../RealtyMapPinInfo';
import { Loader } from '../../../../ui/Loader';
import { FormTemplate } from '../../FormTemplate';
import { ButtonTemplate } from '../../../../ui/buttons/ButtonTemplate';

import {
  initialValues,
  mock,
  selectsDependentNames,
  validationSchemaRealty,
} from '../../../../../lib/mocks/add-realty-mock';
import {
  addDataForDepedentSelects,
  extractInputsFromRealty,
  getDefaultValues,
  getInitialValues,
  handleResponseError,
  transformRealtyValues,
} from '../../../../../utils/data';
import {
  EMPTY_ARRAY,
  countryFranceDefault,
  errorMessages,
  mockAccordionTitles,
  selectsLocationNames,
} from '../../../../../data/constants';
import {
  closeDialog,
  handleResetSelectsRefs,
  showError,
} from '../../../../../utils/ui';
import {
  useAddRealtyMutation,
  useDeleteRealtyMutation,
  useUpdateRealtyMutation,
} from '../../../../../redux/services/realties/realtiesApi';

const ZERO_AS_UNSET_FIELDS = [
  'full_area',
  'land_area',
  'terrace',
  'living_room',
  'kitchen',
  'room',
  'bathroom',
  'bedroom',
  'wc',
  'floor',
  'apartment_number',
  'location_precision',
  'parking_garage_space',
  'parking_parking_space',
  'parking_storage_space',
  'tariff_price',
  'tariff_commission',
  'tariff_agency_estimation_price',
  'tariff_agency_negotiation_price',
];

export const AddRealtyForm = ({
  realty = {},
  classes = false,
  accordionToOpen = '',
  isEdit = false,
  handleClose = () => {},
  prefill = null,
  onCreated = () => {},
  mapPin = null,
}) => {
  const [newInitialValues, setNewInitialValues] = useState({
    ...initialValues,
  });
  const [inputs, setInputs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFirstLoading, setIsFirstLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createdRealtyId, setCreatedRealtyId] = useState(null);
  const createdRealtyIdRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [activeSelect, setActiveSelect] = useState('');
  const [multiSelectRefs, setMultiSelectRefs] = useState({});
  const [inputsRefs, setInputsRefs] = useState({});

  const [selectedCountryId, setSelectedCountryId] = useState(
    countryFranceDefault[0]?.id
  );
  const [selectedCityId, setSelectedCityId] = useState();
  const [selectedStreetId, setSelectedStreetId] = useState();

  const userToken = useSelector(selectUserToken);
  const { data: countries = EMPTY_ARRAY, error: countriesError } =
    useGetCountriesQuery();

  const {
    data: cities,
    isLoading: isCitiesLoading,
    error: citiesError,
  } = useGetCitiesForCountryQuery(
    {
      country_id: selectedCountryId,
      isSettingRedux: true,
    },
    {
      skip: !selectedCountryId,
      refetchOnMountOrArgChange: true,
    }
  );
  const { data: districts, error: districtsError } =
    useGetDistrictsForCityQuery(
      { token: userToken, city_id: selectedCityId, isSettingRedux: true },
      {
        skip: !selectedCityId || !userToken,
        refetchOnMountOrArgChange: true,
      }
    );
  const { data: streets, error: streetsError } = useGetStreetsForCityQuery(
    { token: userToken, city_id: selectedCityId, isSettingRedux: true },
    {
      skip: !selectedCityId || !userToken,
      refetchOnMountOrArgChange: true,
    }
  );
  const { data: streetsNumbers, error: streetsNumbersError } =
    useGetStreetsNumberForStreetQuery(
      { token: userToken, street_id: selectedStreetId, isSettingRedux: true },
      {
        skip: !selectedStreetId || !userToken,
        refetchOnMountOrArgChange: true,
      }
    );
  const {
    data: residences,
    isLoading: isResidencesLoading,
    error: residencesError,
  } = useGetResidencesForCityQuery(
    {
      token: userToken,
      city_id: selectedCityId,
      isSettingRedux: true,
    },
    {
      skip: !selectedCityId || !userToken,
      refetchOnMountOrArgChange: true,
    }
  );

  const [addNewRealty, { isLoading: isAddingLoading }] =
    useAddRealtyMutation();
  const [editRealty, { isLoading: isEditingLoading }] =
    useUpdateRealtyMutation();
  const [deleteRealty, { isLoading: isDeletingLoading, error: deletingError }] =
    useDeleteRealtyMutation();

  const hasClasses = classes && classes.length > 0;

  const handleSubmit = async (values, closeOnSubmit) => {
    // Prevent double-click submissions
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const shouldClose = closeOnSubmit === true;
      setError(null);

      let response;
      let errorStatus;
      const data = { ...values };
      for (const field of ZERO_AS_UNSET_FIELDS) {
        if (field in data && Number(data[field]) === 0) data[field] = null;
      }

      // Use ref for synchronous check (avoids race condition with async setState)
      const existingId = realty?.id || createdRealtyIdRef.current;
      const shouldUpdate = isEdit || createdRealtyIdRef.current;

      if (shouldUpdate) {
        response = await editRealty({
          token: userToken,
          id: existingId,
          data,
        });
        errorStatus = handleResponseError(
          response?.error,
          null,
          () => {
            toast.success('Realty updated successfully');
            closeDialog({ handleClose });
          },
          shouldClose
        );
      } else {
        response = await addNewRealty({ token: userToken, data });

        // Store created ID for subsequent saves (ref for sync, state for UI)
        const newId = response?.data?.id;
        if (!response?.error && newId) {
          createdRealtyIdRef.current = newId;
          setCreatedRealtyId(newId);
          onCreated(newId);
        }

        errorStatus = handleResponseError(
          response?.error,
          null,
          () => {
            toast.success('Realty created successfully');
            closeDialog({ handleClose });
          },
          shouldClose
        );
      }

      showError(errorStatus);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canDelete = isEdit && !!realty?.can?.delete;

  const handleDelete = async () => {
    if (!canDelete) return;

    const realtyId = realty?.id || createdRealtyIdRef.current;
    if (!realtyId) return;

    try {
      const response = await deleteRealty({ token: userToken, id: realtyId });
      if (response?.error) {
        showError(handleResponseError(response.error));
      } else {
        toast.success('Realty deleted successfully');
        closeDialog({ handleClose });
      }
    } catch {
      toast.error('Failed to delete realty');
    }
  };

  const handleResetLocationSelects = name => {
    setActiveSelect(name);
  };

  const handleCountrySelect = async selectedItem => {
    await setSelectedCountryId(selectedItem?.value);
    handleResetLocationSelects(selectsDependentNames.country);
    //await dispatch(addData(cities, setCities));
  };

  const handleCitySelect = async selectedItem => {
    await setSelectedCityId(selectedItem?.value);
    handleResetLocationSelects(selectsDependentNames.city);
    //await dispatch(addData(districts, setDistricts));
    //await dispatch(addData(streets, setStreets));
  };

  const handleStreetSelect = async selectedItem => {
    await setSelectedStreetId(selectedItem?.value);
    handleResetLocationSelects(selectsDependentNames.street);

    //await dispatch(addData(streetsNumbers, setStreetsNumbers));
  };

  const addDataForSelects = async props => {
    const updatedmock = await addDataForDepedentSelects({
      ...props,
      handleCountrySelect,
      handleCitySelect,
      handleStreetSelect,
      mock,
    });

    return updatedmock;
  };

  const updateDepedentSelects = async newRealty => {
    let selectsData = {};
    const countryRealty = newRealty?.[selectsLocationNames.country];
    const cityRealty = newRealty?.[selectsLocationNames.city]?.id;
    const streetRealty = newRealty?.[selectsLocationNames.street]?.id;

    if (countryRealty || countryRealty === 0) {
      await setSelectedCountryId(countryRealty);
      selectsData = { countries, cities };
    }

    if (cityRealty || cityRealty === 0) {
      await setSelectedCityId(cityRealty);
      selectsData = { ...selectsData, districts, streets };
    }

    if (streetRealty || streetRealty === 0) {
      await setSelectedStreetId(streetRealty);
      selectsData = { ...selectsData, streetsNumbers };
    }

    return selectsData;
  };

  const updateMock = async props => {
    // add mode with a prefill
    const valuesRealty = await extractInputsFromRealty(
      isEdit ? realty : prefill ?? realty
    );
    const newRealty = await transformRealtyValues(valuesRealty);

    let selectsData = { ...props };
    let mockWithSelectData = [];

    mockWithSelectData = await addDataForSelects(selectsData);

    if (isEdit) {
      setNewInitialValues(prevValues => ({
        ...getInitialValues(newRealty, prevValues),
        contacts: toContactLinks(realty?.contacts),
      }));

      selectsData = await updateDepedentSelects(newRealty);

      const updatedMock = await getDefaultValues(
        newRealty,
        mockWithSelectData,
        selectsData
      );

      setInputs(updatedMock);
    } else if (prefill) {
      setNewInitialValues(getInitialValues(newRealty, { ...initialValues }));

      selectsData = await updateDepedentSelects(newRealty);

      setInputs(
        await getDefaultValues(newRealty, mockWithSelectData, selectsData)
      );
    } else {
      setNewInitialValues({ ...initialValues });
      setInputs(mockWithSelectData);
    }
  };

  useEffect(() => {
    if (activeSelect) {
      handleResetSelectsRefs(activeSelect, {
        ...multiSelectRefs,
        ...inputsRefs,
      });
    }
  }, [activeSelect]);

  useEffect(() => {
    if (isFirstLoading) {
      const errors = countriesError || citiesError;

      if (countries.length > 0 && cities) {
        updateMock({ countries, cities });
        setIsFirstLoading(false);
      }

      if (errors) {
        setError(errorMessages.data);
        setInputs(mock);
      }

      setIsLoading(false);
    }
  }, [countries, cities]);

  useEffect(() => {
    showError(error);
  }, [error]);

  const formButtonsJSX = formProps => {
    return (
      <div className="flex justify-between">
        <div className="flex gap-2">
          <ButtonTemplate
            text="Save"
            bgColor="bg-purpleColor border-purpleColor"
            isLoadingData={isSubmitting}
            disabled={
              !formProps.dirty ||
              Object.keys(formProps.errors).length > 0 ||
              isSubmitting
            }
            handleClick={() => handleSubmit(formProps.values, false)}
          />
          <ButtonTemplate
            text="Save & Close"
            bgColor="bg-greenColor border-greenColor"
            isLoadingData={isSubmitting}
            disabled={
              !formProps.dirty ||
              Object.keys(formProps.errors).length > 0 ||
              isSubmitting
            }
            handleClick={() => handleSubmit(formProps.values, true)}
          />
        </div>
        {canDelete &&
          (showDeleteConfirm ? (
            <div className="flex gap-2">
              <ButtonTemplate
                text="Confirm"
                bgColor="bg-red-500 border-red-500"
                disabled={isSubmitting}
                handleClick={handleDelete}
              />
              <ButtonTemplate
                text="Cancel"
                handleClick={() => setShowDeleteConfirm(false)}
              />
            </div>
          ) : (
            <ButtonTemplate
              text="Delete"
              icon={Trash}
              isIconText={true}
              bgColor="bg-red-500 border-red-500"
              disabled={isSubmitting}
              handleClick={() => setShowDeleteConfirm(true)}
            />
          ))}
      </div>
    );
  };
  return (
    <>
      <div
        className={cn(
          'flex flex-col gap-3 flex-row flex-wrap w-full min-h-[556px] dialog-with-click',
          {
            [classes]: hasClasses,
          }
        )}
      >
        <div className="flex items-center gap-5">
          <span className="text-sm text-blackColor">
            {realty.id || createdRealtyId
              ? `Editing property ${realty.id || createdRealtyId}`
              : 'New property'}
          </span>
        </div>

        {realty?.can?.claim && (
          <div className="w-full p-2 rounded bg-yellowColor/10 border border-yellowColor/40 text-xs text-blackColor">
            Imported listing. Adding a mandate or a document will take it over
            for you; other agents can then no longer edit it.
          </div>
        )}

        <RealtyMapPinInfo
          realty={realty}
          pendingPin={mapPin}
        />

        {!isLoading && inputs && inputs.length > 0 && (
          <FormTemplate
            initialValues={newInitialValues}
            validationSchema={Yup.object(validationSchemaRealty)}
            handleSubmitForm={handleSubmit}
            classes={'items-start w-full'}
            formClasses={'gap-3 w-full'}
            renderButtons={formButtonsJSX}
          >
            {formProps => (
              <div>
                {Object.entries(mockAccordionTitles).map(([key, title]) => {
                  return (
                    <AccordionInputs
                      realtyId={realty.id}
                      contactsList={realty?.contacts}
                      isEdit={isEdit}
                      title={title}
                      mock={inputs}
                      formProps={formProps}
                      key={key}
                      setMultiSelectRefs={setMultiSelectRefs}
                      setInputsRefs={setInputsRefs}
                      accordionToOpen={accordionToOpen}
                    />
                  );
                })}
              </div>
            )}
          </FormTemplate>
        )}

        {!error && (!inputs || inputs.length < 1) && (
          <div className="flex items-center justify-center pt-6 h-[526px]">
            <Loader
              height={40}
              width={40}
            />
          </div>
        )}
      </div>
    </>
  );
};
